import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import { BannerStyle } from '../../../../typings/enums';
import { useScheduleOrgDeletion } from '../../../hooks/api/internalTools/orgDeletion/useScheduleOrgDeletion';
import { getErrorMessage } from '../../../ReactQuery/errors';
import Banner from '../../generic/Banner';
import Button from '../../generic/Button/Button';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../generic/Button/button-constants';
import ButtonGroup from '../../generic/Button/ButtonGroup';
import InlineLoaderButton from '../../generic/Button/InlineLoaderButton';
import LabeledTextInput from '../../generic/Forms/LabeledTextInput';
import type { InternalToolsOrg } from '../../../../shared/InternalTools/orgs';

type Props = {
  org: InternalToolsOrg;
  show: boolean;
  onHide: () => void;
};

/**
 * Schedules an organization's deletion, 14 days out. The typed name must match the title exactly (case and spaces
 * included); the server checks it again. Server errors show inside the dialog, since the hook doesn't toast them.
 */
export const OrgDeletionDialog: VoidFunctionComponent<Props> = ({ org, show, onHide }) => {
  const [typedTitle, setTypedTitle] = useState('');
  const scheduleMutation = useScheduleOrgDeletion();

  const isTitleMatch = typedTitle === org.title;

  const onModalHide = () => {
    setTypedTitle('');
    scheduleMutation.reset();
    onHide();
  };

  const handleSubmit = () => {
    scheduleMutation.mutate({ organizationId: org.id, typedTitle }, { onSuccess: onModalHide });
  };

  return (
    <Modal
      onHide={() => {
        // Keep the dialog open (no close) while the schedule request is in flight, so a failure always shows here.
        if (!scheduleMutation.isPending) {
          onModalHide();
        }
      }}
      animation
      show={show}
      aria-labelledby="org-deletion-dialog-title"
    >
      <Modal.Header closeButton={!scheduleMutation.isPending}>
        <Modal.Title id="org-deletion-dialog-title">Delete {org.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="margin-bottom-lg">
          <Banner
            title=""
            body="The organization and everything in it will be deleted in 14 days. You can cancel before then."
            bannerStyle={BannerStyle.Medium}
          />
        </div>
        <div className="margin-bottom-md">
          <div>Organization ID: {org.id}</div>
          <div>Title: {org.title}</div>
          <div>Projects: {org.projectCount}</div>
          <div>Users: {org.userCount}</div>
        </div>
        <div className="margin-bottom-md">
          <LabeledTextInput
            label="Type the organization's title to confirm"
            value={typedTitle}
            onChange={setTypedTitle}
            placeholder={org.title}
          />
          {typedTitle !== '' && !isTitleMatch && (
            <p className="text-danger margin-top-xs">
              Must match the organization title exactly, including case and spaces.
            </p>
          )}
        </div>
        {scheduleMutation.isError && (
          <Banner
            title="Couldn't schedule the deletion"
            body={getErrorMessage(scheduleMutation.error)}
            bannerStyle={BannerStyle.High}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button
            type={BUTTON_TYPE.SECONDARY}
            size={BUTTON_SIZE.LARGE}
            isDisabled={scheduleMutation.isPending}
            onClick={onModalHide}
          >
            Cancel
          </Button>
          <InlineLoaderButton
            type={BUTTON_TYPE.ERROR}
            size={BUTTON_SIZE.LARGE}
            isSubmitButton
            isLoading={scheduleMutation.isPending}
            isDisabled={!isTitleMatch || scheduleMutation.isPending}
            onClick={handleSubmit}
          >
            Schedule Deletion
          </InlineLoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};
